import { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Rocket, Heart, DollarSign, Users, Palette, Wrench, Stethoscope, Code, ChevronRight, Star, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { completeMission } from '../../utils/missionHelper';

// Fun career cards for middle schoolers
const careerCards = [
  {
    id: 'game-dev',
    title: 'Video Game Developer',
    emoji: '🎮',
    category: 'Technology',
    color: 'from-blue-500 to-purple-500',
    shortDesc: 'Create the games people play!',
    whatTheyDo: 'Game developers design, code, and create video games. They build worlds, characters, and stories that millions of people enjoy.',
    coolFacts: [
      'Some game devs get to play games as part of their job!',
      'You can work on games for phones, computers, or consoles',
      'Many games are made by teams of 100+ people'
    ],
    skills: ['Coding', 'Creativity', 'Problem Solving', 'Teamwork'],
    education: 'Usually need to learn programming, can start learning now!',
    icon: Code
  },
  {
    id: 'youtuber',
    title: 'Content Creator/YouTuber',
    emoji: '📺',
    category: 'Media',
    color: 'from-red-500 to-pink-500',
    shortDesc: 'Make videos people love to watch!',
    whatTheyDo: 'Content creators make videos, stream games, create tutorials, or share their lives. They build communities of fans.',
    coolFacts: [
      'Top YouTubers can earn millions of dollars',
      'You can start creating content at any age',
      'Many successful creators started as kids'
    ],
    skills: ['Creativity', 'Communication', 'Editing', 'Consistency'],
    education: 'No specific degree needed, but learning video editing helps!',
    icon: Palette
  },
  {
    id: 'doctor',
    title: 'Doctor',
    emoji: '🩺',
    category: 'Healthcare',
    color: 'from-green-500 to-emerald-500',
    shortDesc: 'Help people feel better!',
    whatTheyDo: 'Doctors diagnose illnesses, prescribe treatments, and help people stay healthy. They can specialize in many areas.',
    coolFacts: [
      'There are over 120 different types of doctors',
      'Doctors can work with kids, animals, or even astronauts!',
      'Some doctors do surgery, others just talk to patients'
    ],
    skills: ['Science', 'Caring', 'Problem Solving', 'Communication'],
    education: 'Lots of school! College, then medical school, then training.',
    icon: Stethoscope
  },
  {
    id: 'vet',
    title: 'Veterinarian',
    emoji: '🐾',
    category: 'Animals',
    color: 'from-amber-500 to-orange-500',
    shortDesc: 'Be a doctor for animals!',
    whatTheyDo: 'Vets take care of sick and injured animals. They give vaccines, do surgeries, and help pet owners keep their animals healthy.',
    coolFacts: [
      'Some vets work at zoos with lions and elephants!',
      'Marine vets work with dolphins and whales',
      'You might help dogs, cats, horses, or even snakes'
    ],
    skills: ['Animal Care', 'Science', 'Patience', 'Kindness'],
    education: 'College plus veterinary school (4 more years)',
    icon: Heart
  },
  {
    id: 'athlete',
    title: 'Professional Athlete',
    emoji: '⚽',
    category: 'Sports',
    color: 'from-orange-500 to-red-500',
    shortDesc: 'Play sports for a living!',
    whatTheyDo: 'Pro athletes compete in sports like basketball, soccer, football, or tennis. They train every day to be the best.',
    coolFacts: [
      'Top athletes can earn millions per year',
      'Most start training as young kids',
      'Many athletes also become coaches later'
    ],
    skills: ['Athletic Ability', 'Dedication', 'Teamwork', 'Mental Toughness'],
    education: 'Many play in college before going pro',
    icon: Star
  },
  {
    id: 'engineer',
    title: 'Engineer',
    emoji: '🔧',
    category: 'Engineering',
    color: 'from-slate-500 to-zinc-600',
    shortDesc: 'Build amazing things!',
    whatTheyDo: 'Engineers design and build things - from bridges and buildings to rockets and robots. They solve problems with math and science.',
    coolFacts: [
      'NASA engineers help build rockets',
      'Some engineers make prosthetic limbs for people',
      'Engineers designed your phone!'
    ],
    skills: ['Math', 'Science', 'Problem Solving', 'Creativity'],
    education: 'College degree in engineering',
    icon: Wrench
  },
  {
    id: 'artist',
    title: 'Artist/Animator',
    emoji: '🎨',
    category: 'Arts',
    color: 'from-purple-500 to-pink-500',
    shortDesc: 'Create amazing art and animations!',
    whatTheyDo: 'Artists create visual art - paintings, digital art, animations for movies and games. They bring imagination to life.',
    coolFacts: [
      'Disney and Pixar animators create your favorite characters',
      'Some artists sell their work for millions',
      'You can be an artist for video games, movies, or books'
    ],
    skills: ['Drawing', 'Creativity', 'Patience', 'Digital Tools'],
    education: 'Art school helps, but practice is most important!',
    icon: Palette
  },
  {
    id: 'teacher',
    title: 'Teacher',
    emoji: '📚',
    category: 'Education',
    color: 'from-cyan-500 to-blue-500',
    shortDesc: 'Help others learn and grow!',
    whatTheyDo: 'Teachers share knowledge and help students learn. They can teach any subject to kids, teens, or even adults.',
    coolFacts: [
      'Teachers get summers off!',
      'You could teach anywhere in the world',
      'Many teachers coach sports too'
    ],
    skills: ['Communication', 'Patience', 'Creativity', 'Organization'],
    education: 'College degree and teaching certification',
    icon: Users
  }
];

const CareerModal = ({ career, onClose }) => {
  if (!career) return null;
  const Icon = career.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-slate-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-6 bg-gradient-to-br ${career.color} rounded-t-2xl`}>
          <div className="flex justify-between items-start">
            <div>
              <span className="text-5xl">{career.emoji}</span>
              <h2 className="text-2xl font-bold text-white mt-2">{career.title}</h2>
              <p className="text-white/80">{career.shortDesc}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/20 rounded-full hover:bg-white/30"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-white font-semibold mb-2">What They Do:</h3>
            <p className="text-slate-300">{career.whatTheyDo}</p>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-2">Cool Facts:</h3>
            <ul className="space-y-2">
              {career.coolFacts.map((fact, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300">
                  <Star className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                  {fact}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-2">Skills You'd Use:</h3>
            <div className="flex flex-wrap gap-2">
              {career.skills.map((skill, i) => (
                <span key={i} className="px-3 py-1 bg-slate-700 rounded-full text-sm text-white">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-2">Education:</h3>
            <p className="text-slate-300">{career.education}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function MSCareersPage() {
  const { token } = useAuth();
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [filter, setFilter] = useState('all');
  const viewedRef = useRef(new Set());
  const missionTriggered = useRef(false);

  const categories = ['all', ...new Set(careerCards.map(c => c.category))];
  const filteredCareers = filter === 'all' 
    ? careerCards 
    : careerCards.filter(c => c.category === filter);

  const handleViewCareer = (career) => {
    setSelectedCareer(career);
    viewedRef.current.add(career.id);
    if (viewedRef.current.size >= 3 && !missionTriggered.current && token) {
      missionTriggered.current = true;
      completeMission(token, 'career');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Career Cards</h1>
            <p className="text-slate-300">Explore cool jobs and what people actually do!</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                filter === cat
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {cat === 'all' ? 'All Careers' : cat}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Career Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCareers.map((career, index) => (
          <motion.button
            key={career.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => handleViewCareer(career)}
            className={`p-6 rounded-2xl bg-gradient-to-br ${career.color} text-left transition-transform hover:scale-[1.02] hover:shadow-lg`}
            data-testid={`career-card-${career.id}`}
          >
            <span className="text-4xl">{career.emoji}</span>
            <h3 className="text-xl font-bold text-white mt-3">{career.title}</h3>
            <p className="text-white/80 text-sm mt-1">{career.shortDesc}</p>
            <div className="flex items-center gap-1 mt-4 text-white/80 text-sm">
              <span>Learn more</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </motion.button>
        ))}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedCareer && (
          <CareerModal career={selectedCareer} onClose={() => setSelectedCareer(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
